import type { SessionEvent } from "../core/types"
import { currentPath, isBlockedElement, isMaskedElement } from "./collectors"

/**
 * Core Web Vitals, one report per metric per PAGE VIEW (the same rules as Google's web-vitals):
 *
 * - LCP: the last candidate before the first input / the page is hidden (hard loads only)
 * - CLS: the worst session window of layout shifts (gap 1 s, window 5 s), per page view
 * - INP: the worst interaction (p98 once there are 50+), from Event Timing grouped by interactionId
 * - FCP, TTFB: once per hard load
 * - ROUTE: SPA route change → the DOM settles (no mutation for 100 ms), per soft navigation
 *
 * The old collector sent a CLS event on every layout shift and every LCP candidate (live: 2,442 CLS
 * events for 145 sessions, a fifth of all journey events) and reported first-input delay as INP.
 * Reports carry `view_id` (0 = the hard load, 1.. = route changes) and `nav` (load | route), and a
 * short structural selector of the element to blame (tag#id.class — never its text).
 */

export type VitalName = "LCP" | "INP" | "CLS" | "TTFB" | "FCP" | "ROUTE"
export type Rating = "good" | "needs-improvement" | "poor"

// Google's thresholds (good ≤ first, poor > second). ROUTE uses the INP-like "feels instant" scale.
const THRESHOLDS: Record<VitalName, [number, number]> = {
  LCP: [2500, 4000],
  INP: [200, 500],
  CLS: [0.1, 0.25],
  TTFB: [800, 1800],
  FCP: [1800, 3000],
  ROUTE: [1000, 2500],
}

export function getRating(name: VitalName, value: number): Rating {
  const [good, poor] = THRESHOLDS[name]
  return value <= good ? "good" : value <= poor ? "needs-improvement" : "poor"
}

/** tag#id.class.class of an element — structure only, never its text; masked/blocked elements stay anonymous. */
export function elementSelector(el: Element | null | undefined): string {
  if (!el || typeof el.tagName !== "string") return ""
  if (isBlockedElement(el)) return "[blocked]"
  const tag = el.tagName.toLowerCase()
  if (isMaskedElement(el)) return tag
  const id = el.id && /^[A-Za-z][\w-]{0,40}$/.test(el.id) ? `#${el.id}` : ""
  const classes = Array.from(el.classList || [])
    .filter((c) => /^[A-Za-z_-][\w-]{0,30}$/.test(c))
    .slice(0, 2)
    .map((c) => `.${c}`)
    .join("")
  const parent = el.parentElement && el.parentElement.tagName !== "BODY" && !id ? el.parentElement : null
  const own = `${tag}${id}${classes}`
  return (parent ? `${parent.tagName.toLowerCase()}${parent.id ? `#${parent.id}` : ""} > ${own}` : own).slice(0, 120)
}

function stripQuery(url: string): string {
  try {
    const u = new URL(url, typeof location !== "undefined" ? location.href : undefined)
    return `${u.origin === (typeof location !== "undefined" ? location.origin : "") ? "" : u.origin}${u.pathname}`.slice(0, 200)
  } catch {
    return url.split(/[?#]/)[0].slice(0, 200)
  }
}

interface LayoutShiftLike extends PerformanceEntry {
  value: number
  hadRecentInput: boolean
  sources?: { node?: Node | null; currentRect?: DOMRectReadOnly }[]
}

interface EventTimingLike extends PerformanceEntry {
  interactionId?: number
  processingStart: number
  processingEnd: number
  target?: Node | null
}

/** Per-page-view state; reset on every route change. */
class ViewState {
  cls = 0
  clsWindow = 0
  clsWindowStart = 0
  clsLast = 0
  clsSource = ""
  clsSourceValue = 0
  clsWindowSource = ""
  clsWindowSourceValue = 0
  interactions = new Map<number, { duration: number; target: string; event: string }>()
  reportedCls = -1
  reportedInp = -1
  constructor(public readonly id: number, public readonly nav: "load" | "route", public readonly path: string) {}

  addShift(e: LayoutShiftLike): void {
    if (e.hadRecentInput) return
    const t = e.startTime
    if (this.clsWindow && (t - this.clsLast > 1000 || t - this.clsWindowStart > 5000)) {
      this.clsWindow = 0
      this.clsWindowSource = ""
      this.clsWindowSourceValue = 0
    }
    if (!this.clsWindow) this.clsWindowStart = t
    this.clsWindow += e.value
    this.clsLast = t
    if (e.value > this.clsWindowSourceValue) {
      const node = (e.sources || []).map((s) => s.node).find((n) => n && n.nodeType === 1) as Element | undefined
      this.clsWindowSource = elementSelector(node)
      this.clsWindowSourceValue = e.value
    }
    if (this.clsWindow > this.cls) {
      this.cls = this.clsWindow
      this.clsSource = this.clsWindowSource
      this.clsSourceValue = this.clsWindowSourceValue
    }
  }

  addInteraction(e: EventTimingLike): void {
    const id = e.interactionId
    if (!id) return
    const prev = this.interactions.get(id)
    if (!prev || e.duration > prev.duration) {
      const target = e.target && e.target.nodeType === 1 ? elementSelector(e.target as Element) : prev?.target || ""
      this.interactions.set(id, { duration: e.duration, target, event: e.name })
    }
  }

  /** web-vitals' INP: the worst interaction, skipping one per 50 interactions (≈ p98). */
  inp(): { duration: number; target: string; event: string } | null {
    if (!this.interactions.size) return null
    const sorted = [...this.interactions.values()].sort((a, b) => b.duration - a.duration)
    return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length / 50))]
  }
}

export interface VitalsHandle {
  /** Report this page view's CLS / INP (and LCP if still pending), e.g. before a route change or when hidden. */
  flushView(): void
  /** A soft navigation started: flush the old view, start a new one and time the route render. */
  routeChanged(fromPath: string): void
  stop(): void
}

export function initVitalsObserver(onVital: (ev: SessionEvent) => void): VitalsHandle {
  const noop: VitalsHandle = { flushView() {}, routeChanged() {}, stop() {} }
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return noop

  const observers: PerformanceObserver[] = []
  const drains: (() => void)[] = []
  const cleanups: (() => void)[] = []
  let viewSeq = 0
  const loadView = new ViewState(0, "load", currentPath())
  let view = loadView
  let lcp: { value: number; element: string; url: string } | null = null
  let lcpDone = false
  // Paints after the page was first hidden say nothing about the user's wait (a tab opened in the
  // background "loads" in minutes: live FCP/LCP of 143 s). web-vitals drops them the same way.
  let firstHidden = document.visibilityState === "hidden" ? 0 : Infinity
  const onHidden = () => {
    if (document.visibilityState === "hidden" && firstHidden === Infinity) firstHidden = performance.now()
  }
  document.addEventListener("visibilitychange", onHidden, true)
  cleanups.push(() => document.removeEventListener("visibilitychange", onHidden, true))

  const emit = (name: VitalName, value: number, v: ViewState, extra: Record<string, unknown> = {}) => {
    const score = name === "CLS"
    const rounded = score ? Math.round(value * 1000) / 1000 : Math.round(value)
    onVital({
      type: "vital",
      ts: new Date().toISOString(),
      target: name,
      path: v.path,
      text: `${name}: ${rounded}${score ? "" : "ms"}`,
      meta: { metric: name, value: rounded, unit: score ? "score" : "ms", rating: getRating(name, rounded),
              view_id: v.id, nav: v.nav, ...extra },
    })
  }

  const observe = (type: string, cb: (entries: PerformanceEntry[]) => void, opts: Record<string, unknown> = {}) => {
    try {
      const po = new PerformanceObserver((list) => cb(list.getEntries()))
      po.observe({ type, buffered: true, ...opts } as PerformanceObserverInit)
      observers.push(po)
      // entries queued but not yet delivered: hand them to the callback before reporting
      drains.push(() => {
        const pending = po.takeRecords?.() || []
        if (pending.length) cb(pending)
      })
      return po
    } catch {
      return null
    }
  }

  // TTFB + page load timing (hard load).
  const captureNavigation = () => {
    try {
      const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined
      if (!nav) return
      const ttfb = Math.max(0, nav.responseStart - ((nav as PerformanceNavigationTiming & { activationStart?: number }).activationStart || 0))
      if (ttfb > 0) emit("TTFB", ttfb, view)
      const loadDuration = nav.loadEventEnd > 0 ? Math.round(nav.loadEventEnd - nav.startTime) : Math.round(performance.now())
      if (loadDuration > 0) {
        onVital({
          type: "pageload",
          ts: new Date().toISOString(),
          target: "window",
          path: view.path,
          text: `Page loaded in ${loadDuration}ms`,
          meta: {
            duration_ms: loadDuration,
            ttfb: Math.round(ttfb),
            dom_interactive: Math.round(nav.domInteractive - nav.startTime),
            dom_complete: Math.round(nav.domComplete - nav.startTime),
            is_foreground: document.visibilityState === "visible",
            nav_type: nav.type,
          },
        })
      }
    } catch {}
  }
  if (document.readyState === "complete") setTimeout(captureNavigation, 0)
  else window.addEventListener("load", () => setTimeout(captureNavigation, 0), { once: true })

  observe("paint", (entries) => {
    for (const e of entries) if (e.name === "first-contentful-paint" && e.startTime < firstHidden) emit("FCP", e.startTime, loadView)
  })

  // LCP: keep the latest candidate; it is final at the first input or when the page is hidden.
  const lcpObserver = observe("largest-contentful-paint", (entries) => {
    if (lcpDone) return
    const e = entries[entries.length - 1] as PerformanceEntry & { element?: Element | null; url?: string }
    if (e && e.startTime < firstHidden) lcp = { value: e.startTime, element: elementSelector(e.element), url: e.url ? stripQuery(e.url) : "" }
  })
  const finishLcp = () => {
    if (lcpDone) return
    try {
      const pending = lcpObserver?.takeRecords?.() || []
      const e = pending[pending.length - 1] as (PerformanceEntry & { element?: Element | null; url?: string }) | undefined
      if (e && e.startTime < firstHidden) lcp = { value: e.startTime, element: elementSelector(e.element), url: e.url ? stripQuery(e.url) : "" }
      lcpObserver?.disconnect()
    } catch {}
    lcpDone = true
    if (lcp && lcp.value > 0) emit("LCP", lcp.value, loadView, { element: lcp.element, ...(lcp.url ? { url: lcp.url } : {}) })
  }
  for (const type of ["keydown", "pointerdown"]) {
    const h = () => setTimeout(finishLcp, 0)
    window.addEventListener(type, h, { once: true, capture: true })
    cleanups.push(() => window.removeEventListener(type, h, { capture: true }))
  }

  observe("layout-shift", (entries) => {
    for (const e of entries) view.addShift(e as LayoutShiftLike)
  })

  // INP: Event Timing (Chrome/Edge/Firefox); interactions shorter than 40 ms are never the worst.
  observe("event", (entries) => {
    for (const e of entries) view.addInteraction(e as EventTimingLike)
  }, { durationThreshold: 40 })
  observe("first-input", (entries) => {
    for (const e of entries) view.addInteraction(e as EventTimingLike)
  })

  // ROUTE: from the route change until the DOM has been quiet for 100 ms (capped at 10 s).
  let routeTimer: ReturnType<typeof setTimeout> | null = null
  let routeObserver: MutationObserver | null = null
  let finishRoute: (() => void) | null = null

  /** Report the view's values. Called when the tab is hidden AND on pagehide (both fire when leaving)
   * and again if the tab comes back: a value is re-sent only when it changed — the backend keeps the
   * latest report per view, so a repeat is harmless and a stale 0 would erase a real shift. */
  const flushView = () => {
    if (view.id === 0) finishLcp()
    finishRoute?.()  // leaving before the route settled: what was measured so far
    try {
      for (const d of drains) d()
    } catch {}
    const v = view
    const cls = Math.round(v.cls * 1000) / 1000
    if (cls !== v.reportedCls) {
      emit("CLS", v.cls, v, v.clsSource ? { element: v.clsSource } : {})
      v.reportedCls = cls
    }
    const inp = v.inp()
    if (inp && Math.round(inp.duration) !== v.reportedInp) {
      emit("INP", inp.duration, v, { element: inp.target, event: inp.event, interactions: v.interactions.size })
      v.reportedInp = Math.round(inp.duration)
    }
  }

  const routeChanged = (fromPath: string) => {
    flushView()
    const start = performance.now()
    viewSeq += 1
    const pending = new ViewState(viewSeq, "route", currentPath())
    view = pending
    routeObserver?.disconnect()
    if (routeTimer) clearTimeout(routeTimer)
    let last = start
    const finish = () => {
      routeObserver?.disconnect()
      routeObserver = null
      if (routeTimer) clearTimeout(routeTimer)
      routeTimer = null
      finishRoute = null
      const duration = Math.max(0, last - start)
      const v = new ViewState(pending.id, "route", currentPath())
      emit("ROUTE", duration, v, { from_path: fromPath })
    }
    // view ended before the route settled: report what was measured — unless nothing rendered yet
    finishRoute = () => {
      if (last > start) return finish()
      routeObserver?.disconnect()
      routeObserver = null
      if (routeTimer) clearTimeout(routeTimer)
      routeTimer = null
      finishRoute = null
    }
    const arm = () => {
      if (routeTimer) clearTimeout(routeTimer)
      routeTimer = setTimeout(finish, performance.now() - start > 10_000 ? 0 : 100)
    }
    if (typeof MutationObserver !== "undefined") {
      routeObserver = new MutationObserver(() => {
        last = performance.now()
        arm()
      })
      routeObserver.observe(document.body || document.documentElement, { childList: true, subtree: true, attributes: false })
    }
    arm()
  }

  return {
    flushView,
    routeChanged,
    stop() {
      routeObserver?.disconnect()
      if (routeTimer) clearTimeout(routeTimer)
      for (const c of cleanups) c()
      for (const o of observers) {
        try {
          o.disconnect()
        } catch {}
      }
    },
  }
}
